import React, {
  useState, useRef, useEffect, useMemo,
} from 'react';
import { Button } from '@openedx/paragon';
import { RiArrowDownSLine, RiArrowUpSLine } from '@remixicon/react';
import { FormattedMessage, useIntl } from '@edx/frontend-platform/i18n';

import messages from './messages';

const COLLAPSED_MAX_HEIGHT = 144;

// Course "About" overviews authored in the CMS often ship `<style>`/`<link>` blocks with
// un-scoped, global selectors (body, h1, p, …). Injected verbatim they leak across the
// whole app. Strip those tags (and `<script>`) so only the markup remains — inline styles
// and classes don't leak, and the app already styles the content via `.about-body` rules.
const sanitizeOverviewHtml = (raw) => {
  if (!raw || typeof window === 'undefined' || !window.DOMParser) return raw;
  try {
    const doc = new DOMParser().parseFromString(raw, 'text/html');
    doc.querySelectorAll('style, script, link').forEach((el) => el.remove());
    return doc.body.innerHTML;
  } catch (e) {
    return raw;
  }
};

const AboutSection = ({ html }) => {
  const intl = useIntl();
  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const contentRef = useRef(null);

  const safeHtml = useMemo(() => sanitizeOverviewHtml(html), [html]);

  useEffect(() => {
    if (!contentRef.current) return;
    setIsOverflowing(contentRef.current.scrollHeight > COLLAPSED_MAX_HEIGHT);
  }, [safeHtml]);

  if (!safeHtml) return null;

  return (
    <section className="course-detail__about">
      <h2 className="course-detail__about-title">
        <FormattedMessage {...messages.aboutHeading} />
      </h2>
      <div className="course-detail__about-content">
        <div
          ref={contentRef}
          className={`course-detail__about-body${expanded ? ' course-detail__about-body--expanded' : ''}`}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: safeHtml }}
        />
        {isOverflowing && !expanded && <div className="course-detail__about-fade" aria-hidden />}
      </div>
      {isOverflowing && (
        <Button
          variant="tertiary"
          size="sm"
          className="course-detail__see-more"
          iconAfter={expanded ? RiArrowUpSLine : RiArrowDownSLine}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded
            ? intl.formatMessage(messages.aboutSeeLess)
            : intl.formatMessage(messages.aboutSeeMore)}
        </Button>
      )}
    </section>
  );
};

export default AboutSection;
